import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Sparkles, Brain, Heart, Zap, ArrowRight } from "lucide-react";

const StrengthsDashboardCard = () => {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch(`${API}/strengths/profile`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) return null;

  // CTA for users who haven't completed
  if (!data?.completed) {
    return (
      <Link to="/strengths" className="block" data-testid="strengths-cta-link">
        <Card className="border border-[#FFD507]/40 bg-gradient-to-r from-[#FFD507]/10 to-[#1E3A8A]/5 overflow-hidden hover:border-[#FFD507] hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer" data-testid="strengths-cta-card">
          <CardContent className="p-5 md:p-6 flex items-center gap-5 flex-wrap">
            <div className="w-12 h-12 rounded-full bg-[#FFD507] flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-[#0F1B3D]" />
            </div>
            <div className="flex-1 min-w-[220px]">
              <p className="font-heading font-bold text-slate-900 text-lg group-hover:text-[#1E3A8A]">Discover your strengths</p>
              <p className="text-sm text-slate-600 mt-0.5">
                A 5-minute, strengths-based profile blending your Working Style, top themes, values, and Working Genius.
              </p>
            </div>
            <Button className="btn-primary pointer-events-none" data-testid="strengths-take-btn">
              Start the assessment <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      </Link>
    );
  }

  const profile = data.profile;
  const themes = (profile.top_themes_hydrated || []).slice(0, 3);
  const values = (profile.top_values_hydrated || []).slice(0, 3);
  const geniuses = profile.working_genius_split?.geniuses || [];

  return (
    <Card className="border border-slate-200" data-testid="strengths-summary-card">
      <CardContent className="p-5 md:p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-[#FFD507]/20 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-[#1E3A8A]" />
            </div>
            <div>
              <p className="font-heading font-bold text-slate-900 text-base">Your Strengths Profile</p>
              <p className="text-xs text-slate-500">Saved · share or retake anytime</p>
            </div>
          </div>
          <Link to="/strengths">
            <Button size="sm" variant="outline" data-testid="strengths-view-btn">View full profile <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>
          </Link>
        </div>

        {profile.ai_narrative && (
          <p className="text-sm text-slate-700 italic mb-4 line-clamp-3" data-testid="strengths-mini-narrative">
            {profile.ai_narrative}
          </p>
        )}

        <div className="grid sm:grid-cols-3 gap-3">
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1 mb-1">
              <Brain className="w-3 h-3" /> Top themes
            </p>
            <p className="text-sm text-slate-900 font-medium">
              {themes.length ? themes.map((t) => t.name).join(" · ") : "—"}
            </p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1 mb-1">
              <Heart className="w-3 h-3" /> Top values
            </p>
            <p className="text-sm text-slate-900 font-medium">
              {values.length ? values.map((v) => v.name).join(" · ") : "—"}
            </p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1 mb-1">
              <Zap className="w-3 h-3" /> Working Geniuses
            </p>
            <p className="text-sm text-slate-900 font-medium">
              {geniuses.length ? geniuses.map((g) => g.name).join(" · ") : "—"}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default StrengthsDashboardCard;
